/* eslint-disable react-hooks/exhaustive-deps */
import React, { FC, useMemo } from 'react';
import moment from 'moment';
import Styled from './styles';
import useHook from './hook';
import useCurrentSnap from '../../../../core/hooks/useCurrentSnap';
import { Typography } from '../../../../shared/typography';
import rooms from '../../../../assets/images/team.svg';
import download from '../../../../assets/images/download.svg';
import share from '../../../../assets/images/share.svg';
import eye from '../../../../assets/images/heart.svg';
import details from '../../../../assets/images/details.png';
import deleteIcon from '../../../../assets/images/new-delete.svg';
import activeEye from '../../../../assets/images/heart-active.svg';
import pencil from '../../../../assets/images/adjust.svg';
import bookmark from '../../../../assets/images/bookmark.svg';
import activeBookmark from '../../../../assets/images/bookmark-white.svg';
import EditPopup from '../../../../shared/edit-popup';
import { Tooltip } from '@mui/material';

type IProps = {
	item: any;
	originalName: string;
};

const Chart: FC<IProps> = ({ item, originalName }) => {
	const {
		state,
		onDelete,
		exportSnap,
		addToSnapshots,
		removeSnapshot,
		addToFavorites,
		removeFavorite,
		onShare,
		onOpenInvites,
		toggleLoader,
		saveSettings,
		openDetails,
		useComponentVisible,
	} = useHook();
	const { ref } = useComponentVisible();
	const { currentChart } = useCurrentSnap({ item });
	const { Component, defaultFields, adapter, type, ...rest } =
		currentChart || {};
	const {
		capturedInput,
		timeRange,
		grouping,
		sorting,
		updatedAt,
		isSnapshot,
		isFavorite,
		description,
	} = item;
	const SALESFORCE_ICON =
		'https://getrevvy.s3.us-east-2.amazonaws.com/logos/salesforce.png';
	const OUTREACH_ICON =
		'https://getrevvy.s3.us-east-2.amazonaws.com/logos/outreach.png';
	const {
		isDeleting,
		isSnapshoting,
		isCopied,
		isLoadingMembers,
		isVisibleEdit,
		isSavingSettings,
		isDetailsOpen,
	} = state;

	const showNoData = useMemo(
		() =>
			Array.isArray(item?.responsePayload)
				? !item?.responsePayload?.length
				: !item?.responsePayload?.itemsList.length,
		[item]
	);

	const options = useMemo(
		() => [
			{
				title: 'Team room',
				image: <img src={rooms} style={{ width: 24, marginLeft: -2 }} alt="" />,
				callback: (type: string, item: any) => onOpenInvites(item._id),
				isLoading: isLoadingMembers,
			},
			{
				title: 'Adjust',
				image: <img src={pencil} style={{ width: 20 }} alt="" />,
				callback: () => toggleLoader('isVisibleEdit'),
				isLoading: false,
			},
			{
				title: 'Snapshots',
				image: (
					<img
						src={isSnapshot ? activeBookmark : bookmark}
						style={{ width: 20, marginLeft: 1 }}
						alt=""
					/>
				),
				callback: (type: string, item: any) =>
					isSnapshot ? removeSnapshot(item._id) : addToSnapshots(item._id),
				isLoading: isSnapshoting,
				color: 'white',
				titleColor: isSnapshot ? 'hoverBtn' : 'black',
			},
			{
				title: 'Delete',
				image: <img src={deleteIcon} style={{ width: 20 }} alt="" />,
				callback: (type: string, item: any) => onDelete(item._id),
				isLoading: isDeleting,
				titleColor: 'red',
				hoverText: 'red',
			},
		],
		[state]
	);

	const date = moment(updatedAt).format('DD.MM.YY');
	const next = timeRange.range === 'next';

	return (
		<Styled.Wrapper>
			<Styled.Header>
				<div>
					<Styled.Title>
						<Typography type="king" size={'chartTitle'}>
							{capturedInput}
						</Typography>
					</Styled.Title>
					<Styled.Date>
						{!item?.source || item.source === 'salesforce' ? (
							<img
								src={SALESFORCE_ICON}
								style={{ width: 33, marginRight: 15 }}
								alt="salesforce icon"
							/>
						) : (
							<img
								src={OUTREACH_ICON}
								style={{ width: 33, marginRight: 15 }}
								alt="outreach icon"
							/>
						)}
						<Typography size="normal" color="black">
							{`Created: ${date}`}
						</Typography>
					</Styled.Date>
				</div>
				<Styled.Panel>
					<Styled.Options>
						<Styled.CircleButtonsWrapper>
							<Tooltip title="Favorites" arrow>
								<Styled.CircleButton
									onClick={() =>
										isFavorite
											? removeFavorite(item._id)
											: addToFavorites(item._id)
									}
								>
									<img
										src={isFavorite ? eye : activeEye}
										style={{ width: 19 }}
										alt=""
									/>
								</Styled.CircleButton>
							</Tooltip>
							<Tooltip title="Download" arrow>
								<Styled.CircleButton onClick={() => exportSnap(item._id)}>
									<img src={download} style={{ width: 20 }} alt="" />
								</Styled.CircleButton>
							</Tooltip>
							<Tooltip title={isCopied ? 'Copied' : 'Share'} arrow>
								<Styled.CircleButton onClick={() => onShare(item._id)}>
									<img src={share} style={{ width: 15 }} alt="" />
								</Styled.CircleButton>
							</Tooltip>
						</Styled.CircleButtonsWrapper>
						<Styled.DetailsButton onClick={openDetails}>
							<img src={details} style={{ width: 5 }} alt="" />
						</Styled.DetailsButton>
						{isDetailsOpen && (
							<Styled.DetailsContainer ref={ref}>
								{options.map(
									(option: any, i: number) =>
										option !== null && (
											<Styled.Option
												key={i}
												style={{
													height: 40,
													width: '100%',
													marginBottom: 2,
													marginTop: 2,
													borderRadius: 10,
													alignItems: 'center',
												}}
												color={option.color ?? 'white'}
												title={option.title}
												titleColor={option.titleColor ?? 'black'}
												borderWidth={1}
												borderColor="white"
												loaderColor="black"
												type="button"
												fontS="normal"
												isBorder
												btnType="options"
												isLoading={option.isLoading}
												isDisabled={option.isLoading || option.isDisabled}
												image={option.image}
												hover="grey"
												hoverText={option.hoverText ?? 'black'}
												callBack={() => option.callback(originalName, item)}
											/>
										)
								)}
							</Styled.DetailsContainer>
						)}
					</Styled.Options>
					{isVisibleEdit ? (
						<EditPopup
							data={item}
							toggleModal={() => toggleLoader('isVisibleEdit')}
							isLoading={isSavingSettings}
							callback={saveSettings}
							type={type}
						/>
					) : null}
				</Styled.Panel>
			</Styled.Header>
			<Styled.Header></Styled.Header>
			{description && (
				<Styled.Header>
					<Styled.Title>
						<Typography type="king" size="large">
							{description}
						</Typography>
					</Styled.Title>
				</Styled.Header>
			)}
			<Styled.StatusContainer>
				{timeRange?.field && timeRange?.from && timeRange?.to && (
					<Styled.Status color="btnGreen">
						<Typography color={'black'} size="normal" type="bold">
							{`${timeRange?.field?.replace(/([A-Z])/g, ' $1').trim()}: `}
						</Typography>
						<Typography color={'black'} size="normal">
							{`${moment(timeRange?.from).format('D.M.YY')} - ${moment(
								timeRange?.to
							).format('D.M.YY')}`}
						</Typography>
					</Styled.Status>
				)}
				{grouping?.groupingLevel1 && grouping?.groupingLevel2 && (
					<Styled.Status color="btnYellow">
						<Typography color={'black'} size="normal" type="bold">
							{'Grouping: '}
						</Typography>
						<Typography color={'black'} size="normal">
							{`${grouping?.groupingLevel1?.replace(/([A-Z])/g, ' $1')}`}
							{`${
								grouping?.groupingLevel2 !== 'NoGrouping'
									? ', ' + grouping?.groupingLevel2?.replace(/([A-Z])/g, ' $1')
									: ''
							}`}
						</Typography>
					</Styled.Status>
				)}
				{sorting?.field &&
					sorting?.order &&
					sorting?.order !== 'No Sorting' && (
						<Styled.Status color="btnGreen">
							<Typography color={'black'} size="normal" type="bold">
								{'Sorting: '}
							</Typography>
							<Typography color={'black'} size="normal">
								{`${sorting?.field
									?.replace(/([A-Z])/g, ' $1')
									.trim()
									.toLowerCase()} by ${sorting?.order?.toLowerCase()}`}
							</Typography>
						</Styled.Status>
					)}
			</Styled.StatusContainer>

			{Component && !showNoData && !next && (
				<Component
					data={adapter()}
					defaultFields={defaultFields}
					isPadding={false}
					isDetail={true}
					grouping={item?.grouping}
					{...rest}
				/>
			)}
			{(next || showNoData) && (
				<Styled.NoDataBlock>
					<Typography type="king" size={'king'}>
						No records found. Try changing criteria
					</Typography>
				</Styled.NoDataBlock>
			)}
		</Styled.Wrapper>
	);
};

export default React.memo(Chart);
